import React, {useEffect, useState} from 'react';
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import {fetchApi} from "../utils/fetch";
import {presentCqProductQuiz} from "./cqProductPresentation";
import "survey-core/defaultV2.min.css";
import './survey_js_custom.css';
// Must load AFTER the generated theme map: it overrides defaultV2's rating
// layout so the 7-point scale never splits across lines on narrow screens
// (#547). Kept separate because survey_js_custom.css is generated and must
// not be hand-edited.
import './survey_rating_layout.css';
import '../QuizEditor.css';

function warnBeforeClosing(e) {
  e.preventDefault();
  e.returnValue = '';
}

// `isCqProduct` DEFAULTS TO FALSE, and that direction is deliberate (#575).
// The predicate is computed in Ruby from Quiz::CQ_PRODUCT_TYPE_NAME — the same
// constant the invite body and mail subject already use — so this component
// never re-derives it and there is one place the rule lives.
//
// It is passed explicitly rather than read off the serialized quiz. `type_name`
// IS currently present in that payload (Quiz has no as_json override, so every
// column is serialized), which is exactly why relying on it would be fragile:
// adding an as_json override later — an ordinary thing to do to trim a payload
// — would silently drop the field, the guard would silently go false, and a
// legacy respondent would silently be told they are taking the CQ again. An
// explicit prop turns that into a visible edit at the mount point.
//
// When the flag is missing, this screen says NOTHING about which product it is.
// Omitting the name is never a false statement; naming the wrong instrument is.
export default function SurveyTaker({assessment, quiz: {uuid: quizId, quiz}, feedback = false, formAssignment = null, isCqProduct = false}) {
  const [showSurvey, setShowSurvey] = useState(feedback);

  const {uuid: assessmentId} = assessment || {};
  // cq_product only: two respondent-visible strings in the pinned seed are
  // corrected for display (#662, see cqProductPresentation.js). Other quizzes
  // are handed to SurveyJS as served.
  const survey = new Model(isCqProduct ? presentCqProductQuiz(quiz) : quiz);

  // Phase-5: server-assigned rotation form (A/B/C). Set the `form` value directly (the
  // field's defaultValueExpression already ran at Model construction, so a late variable
  // set alone falls back to 'A'); also set the variable for expression consistency. This
  // is what the rotation pages' `visibleIf: {form}='C'` read, so each respondent sees
  // exactly their assigned block.
  if (formAssignment) {
    survey.setVariable('form_param', formAssignment);
    survey.setValue('form', formAssignment);
  }

  useEffect(() => {
    if (showSurvey && !feedback) window.addEventListener('beforeunload', warnBeforeClosing);
  }, [showSurvey]);

  // --- Phase 4: Track FC ranking question interactions ---
  // Detects whether participants actually reordered drag-and-drop ranking items
  // vs. accepting the default display order (a careless responding signal).
  const rankingInteractions = {};
  survey.onValueChanged.add((sender, options) => {
    const question = sender.getQuestionByName(options.name);
    if (question && question.getType() === 'ranking') {
      rankingInteractions[options.name] = (rankingInteractions[options.name] || 0) + 1;
    }
  });

  // --- Phase 4: Save partial results on page navigation ---
  survey.onPartialSend.add((sender) => {
    if (!assessmentId) return;
    // Inject ranking interaction counts into data before saving
    const dataWithInteractions = {
      ...sender.data,
      ...Object.fromEntries(
        Object.entries(rankingInteractions).map(([k, v]) => [`${k}_interactions`, v])
      ),
    };
    fetchApi(`/take/${assessmentId}/save_progress`, {
      method: 'PATCH',
      body: JSON.stringify({
        results: dataWithInteractions,
        quiz_id: quizId,
      }),
    }).catch(() => {
      // Silently fail on partial saves — data will be sent on completion
      console.warn('Partial save failed — will retry on next page or completion');
    });
  });

  survey.onCompleting.add((sender, _options) => {
    window.removeEventListener('beforeunload', warnBeforeClosing);
  });

  survey.onComplete.add((sender, {showSaveError, showSaveInProgress, showSaveSuccess}) => {
    const url = assessmentId ? `/take/${assessmentId}/complete` : '/take/complete';
    showSaveInProgress('Saving your assessment, please wait...');
    // Inject ranking interaction counts into final save data
    const dataWithInteractions = {
      ...sender.data,
      ...Object.fromEntries(
        Object.entries(rankingInteractions).map(([k, v]) => [`${k}_interactions`, v])
      ),
    };
    fetchApi(url, {
      method: 'PATCH',
      body: JSON.stringify({
        results: dataWithInteractions,
        quiz_id: quizId,
        feedback,
      }),
    }).then(({success}) => {
      if (success) {
        showSaveSuccess('Saved!');
        if (!!quiz.navigateToUrl) setTimeout(() => window.location.href = quiz.navigateToUrl, 3000);
      } else {
        showSaveError('There was an issue saving your results...');
      }
    })
    .catch(() => {
      showSaveError('Request error: There was an issue saving your results, please click, "Try again"...');
    });
  });

  return (
    <>
      { showSurvey
        ?
          // The #547 rating-layout rules are scoped to this wrapper, NOT applied
          // globally. survey_rating_layout.css squeezes the seven chips to 1/7 of
          // the row, which is only correct for the CQ product form; SurveyTaker
          // also renders every Phase-4/Phase-5 research instrument, and 1385 of
          // their rating items across 34 seed forms are endpoint-anchored (the
          // per-directory breakdown is in survey_rating_layout.css), so an
          // unscoped selector would restyle live research forms. `isCqProduct` defaults to false
          // (see the note above), so a missing flag means no rules apply and the
          // pre-#547 layout is what renders -- degraded, never wrong.
          //
          // The wrapper is rendered ONLY on the cq_product path, rather than
          // always with a conditional class. A research form therefore keeps the
          // exact DOM it has today: <Survey> stays the direct child of the
          // react_component mount point (`class: 'h-full'`, take/show.html.erb),
          // with no extra unstyled layer between them. "Inert" should mean the
          // tree is unchanged too, not just that no rule matches.
          (isCqProduct
            ? <div className="cq-rating-layout"><Survey model={survey} /></div>
            : <Survey model={survey} />)
        : (
          <div className="mx-auto h-full flex flex-col justify-center items-center">
            <div>
              <div className="mx-auto max-w-prose text-lg">
                <h1>
                  {/* Rendered ONLY for cq_product (#575, fixed 2026-08-13). SurveyTaker
                      renders for every quiz-backed SurveyJS assessment, so before this guard
                      a legacy respondent reached a screen naming an instrument they were not
                      taking -- the correct name in the invite (post-#574), then the wrong one
                      here, then the correct one on the form, with the wrong one the most
                      prominent of the three. #536's rule, restated in #574, is that CQ naming
                      is learner-facing for the cq_product path only.

                      The string itself must stay byte-identical to the survey title the form
                      serves immediately after this splash (form product-v1.1.1). These two
                      screens are consecutive in one flow, so a mismatch shows a respondent two
                      different product names back to back. Naming PI-decided 2026-08-03 (#536).

                      Non-cq_product renders NO eyebrow rather than a substitute. It must not
                      be "Coachability@Work" -- that mark is retired (#574; PI 2026-08-11, the
                      program is Coachability@Scale) -- and no other generic label has been
                      approved for learner-facing use here. */}
                  {isCqProduct && (
                    <span className="block text-center text-lg font-semibold text-human-600">Coachability Quotient (CQ)</span>
                  )}
                  <span
                    className="mt-2 block text-center text-3xl font-bold leading-8 tracking-tight text-ink sm:text-4xl">Before you begin...</span>
                </h1>
                <p className="mt-8 text-xl leading-8 text-ink-muted text-center">Please try to complete this assessment in one
                  sitting.</p>
                {/* Whether anything is persisted before completion depends on the form: onPartialSend
                    (above) only fires when the quiz sets sendResultOnPageNext. cq_product does; a
                    quiz created through the internal API does not, by default. Neither sentence may
                    be stated unconditionally -- the old copy asserted "not saved" for every form,
                    which is false wherever partial send is on. */}
                {quiz?.sendResultOnPageNext ? (
                  <p className="mt-8 text-xl leading-8 text-ink-muted text-center">Also, note that your responses
                    are <strong>recorded as you go</strong>, but the Assessment <strong>cannot be resumed</strong> &mdash; if
                    you leave this page before completing it, you'll need to start over from the beginning.</p>
                ) : (
                  <p className="mt-8 text-xl leading-8 text-ink-muted text-center">Also, note that if you <strong>leave this
                    page before completing</strong> the Assessment, your answers will <strong>not be saved</strong> and
                    you'll have to start over.</p>
                )}
                <button
                  className="mt-8 mx-auto block font-medium text-lg bg-accent hover:bg-accent-press max-w-20 rounded-full text-ink [border:var(--border-accent)] px-5 py-3 transition duration-150 ease-out active:scale-[.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2"
                  onClick={() => setShowSurvey(true)}>Begin assessment
                </button>
              </div>
            </div>
          </div>
      ) }
    </>
  );
}
